import {
  UserRouteActionV1Model,
  getHandledJobUids
} from '@core/models/entities/implementations/user-route-action-v1';
import { UserRouteV1Model } from '@core/models/entities/implementations/user-route-v1';
import {
  CarriedItem,
  CarriedJob,
  PlacementTarget,
  PlanEditGesture
} from '@core/models/plan-edit-gesture.model';

import { isPredictedActionUid } from './predictions';
import { routeStops } from './route-timing';

/**
 * The + New route row's key: the timeline marks its lane with it, and a move dropped there names
 * it as its route until the new route's form gives the route its ref.
 */
export const NEW_ROUTE_ROW = 'new-route';
const ROUTE_REF_PREFIX = `${NEW_ROUTE_ROW}-`;
let routeRefsAllocated = 0;

/**
 * A label for the route a batch opens, until DL mints its uid. Each gesture gets its own, so two
 * routes opening at once are shown as two rows; DL takes any label that names no route.
 */
export function allocateRouteRef(): string {
  routeRefsAllocated += 1;
  return `${ROUTE_REF_PREFIX}${routeRefsAllocated}`;
}

/** Whether `uid` is the ref of a route a gesture still awaiting its answer opens. */
export function isRouteRef(uid: string): boolean {
  return uid.startsWith(ROUTE_REF_PREFIX);
}

/** The move aimed at the end of the route its batch opens as `routeRef`. */
export function moveIntoRoute(move: PlanEditGesture, routeRef: string): PlanEditGesture {
  const placement = { kind: 'EndOf' as const, routeUid: routeRef };
  switch (move.kind) {
    case 'move-stop':
    case 'move-job':
    case 'move-tour':
      return { ...move, placement };
    default:
      return move;
  }
}

/** The + New route row, which takes any carried item. */
export function newRouteTarget(): PlacementTarget {
  return {
    routeUid: NEW_ROUTE_ROW,
    placement: { kind: 'EndOf', routeUid: NEW_ROUTE_ROW },
    label: 'end'
  };
}

export interface TargetRow {
  uid: string;
  route: UserRouteV1Model;
  validated: boolean;
}

/**
 * The stops whose gaps would leave what is carried exactly where it is, on its own route. A job
 * dropped back before a stop that keeps other jobs splits it off, so only a stop's only job
 * counts as staying put.
 */
function carriedStopUids(route: UserRouteV1Model, carried: CarriedItem): string[] {
  switch (carried.kind) {
    case 'tour':
      return carried.actionUids;
    case 'stop':
      return [carried.actionUid];
    case 'job': {
      const from = route.actions.find((candidate) => candidate.uid === carried.actionUid);
      return from && getHandledJobUids(from).length === 1 ? [carried.actionUid] : [];
    }
  }
}

/**
 * Whether a stop reordered within its route may land before `beforeUid` (`null`: the route's end)
 * without DL refusing it as `job_precedence_broken`: it stays after the pickup of every job it
 * delivers and before the delivery of every job it picks up.
 */
function keepsPrecedence(
  stops: UserRouteActionV1Model[],
  carriedUid: string
): (beforeUid: string | null) => boolean {
  const carried = stops.find((stop) => stop.uid === carriedUid);
  const picked = jobsOfKind(carried, 'pickup');
  const delivered = jobsOfKind(carried, 'delivery');
  let lowest = 0;
  let highest = stops.length;
  stops.forEach((stop, index) => {
    if (stop.uid === carriedUid) return;
    if ([...jobsOfKind(stop, 'pickup')].some((job) => delivered.has(job))) {
      lowest = Math.max(lowest, index + 1);
    }
    if ([...jobsOfKind(stop, 'delivery')].some((job) => picked.has(job))) {
      highest = Math.min(highest, index);
    }
  });
  return (beforeUid) => {
    const index =
      beforeUid === null ? stops.length : stops.findIndex((stop) => stop.uid === beforeUid);
    return index >= lowest && index <= highest;
  };
}

/** The positions of one route, merge positions first, then each gap, then the end. */
export function positionsFor(row: TargetRow, carried: CarriedItem): PlacementTarget[] {
  // DL knows the route a pending batch opens by no uid a later gesture could name.
  if (row.validated || isRouteRef(row.uid)) {
    return [];
  }
  const routeUid = row.uid;
  // DL knows no stop a pending gesture predicts, so none can anchor a placement.
  const stops = routeStops(row.route).filter((stop) => !isPredictedActionUid(stop.uid));
  const ownUids =
    row.uid === carried.routeUid
      ? new Set(carriedStopUids(row.route, carried))
      : new Set<string>();
  const lastOwn = stops.map((s) => ownUids.has(s.uid)).lastIndexOf(true);
  const stayingPut = new Set<string | null>(ownUids);
  if (lastOwn >= 0) {
    stayingPut.add(stops[lastOwn + 1]?.uid ?? null);
  }

  const merges: PlacementTarget[] = [];
  // Within its own route a stop moves as a reorder, which cannot merge it into another stop.
  const mergesHere =
    carried.kind === 'job' || (carried.kind === 'stop' && row.uid !== carried.routeUid);
  if (mergesHere) {
    for (const stop of stops) {
      const isOwn = row.uid === carried.routeUid && stop.uid === carried.actionUid;
      if (stop.site_uid === carried.siteUid && !isOwn) {
        merges.push({
          routeUid,
          placement: { kind: 'MergedInto', routeUid, actionUid: stop.uid },
          label: 'merge'
        });
      }
    }
  }
  const allowed =
    carried.kind === 'stop' && row.uid === carried.routeUid
      ? keepsPrecedence(stops, carried.actionUid)
      : () => true;
  const gaps: PlacementTarget[] = stops
    .filter((stop) => !stayingPut.has(stop.uid) && allowed(stop.uid))
    .map((stop) => ({
      routeUid,
      placement: { kind: 'Before', routeUid, actionUid: stop.uid },
      label: 'gap'
    }));
  const end: PlacementTarget[] =
    stayingPut.has(null) || !allowed(null)
      ? []
      : [{ routeUid, placement: { kind: 'EndOf', routeUid }, label: 'end' }];
  return [...merges, ...gaps, ...end];
}

function jobsOfKind(
  action: UserRouteActionV1Model | undefined,
  kind: 'pickup' | 'delivery'
): Set<string> {
  return new Set(
    (action?.movements ?? []).flatMap((m) =>
      m.job_uid && isKind(m, kind) ? [m.job_uid] : []
    )
  );
}

const isKind = (movement: UserRouteActionV1Model['movements'][number], kind: string) =>
  movement.operation_kind?.toLowerCase() === kind;

/**
 * The jobs the stop loads that another stop of its route delivers. DL anchors a move on a job's
 * delivery and carries its pickup along, so such a job moved from this stop would land its
 * delivery where the planner aimed the loading.
 *
 * A reverse-logistics job is anchored on its pickup instead, which the route does not say: its
 * stops are judged as a forward job's would be.
 */
export function jobsLoadedForDeliveryOnItsRoute(
  route: UserRouteV1Model,
  actionUid: string
): Set<string> {
  const action = route.actions.find((candidate) => candidate.uid === actionUid);
  const loaded = new Set(
    (action?.movements ?? []).filter((m) => isKind(m, 'pickup')).map((m) => m.job_uid)
  );
  const deliveredElsewhere = new Set<string>();
  if (loaded.size === 0) return deliveredElsewhere;
  for (const other of route.actions) {
    if (other.uid === actionUid) continue;
    for (const m of other.movements ?? []) {
      if (m.job_uid && isKind(m, 'delivery') && loaded.has(m.job_uid)) {
        deliveredElsewhere.add(m.job_uid);
      }
    }
  }
  return deliveredElsewhere;
}

/** Whether the stop loads any job its route delivers, which keeps the whole stop on its route. */
export function loadsJobsDeliveredOnItsRoute(
  route: UserRouteV1Model,
  actionUid: string
): boolean {
  return jobsLoadedForDeliveryOnItsRoute(route, actionUid).size > 0;
}

/**
 * A job carried out of a stop. Its merge site is that of the stop serving its primary operation,
 * which DL moves: this stop, unless this stop only loads it for a delivery made later on its
 * route.
 */
export function carriedJob(
  route: UserRouteV1Model,
  actionUid: string,
  jobUid: string
): CarriedJob {
  const deliveredLater = jobsLoadedForDeliveryOnItsRoute(route, actionUid).has(jobUid);
  const primary = route.actions.find((candidate) =>
    deliveredLater
      ? candidate.uid !== actionUid &&
        (candidate.movements ?? []).some(
          (m) => m.job_uid === jobUid && isKind(m, 'delivery')
        )
      : candidate.uid === actionUid
  );
  return {
    kind: 'job',
    routeUid: route.uid,
    actionUid,
    siteUid: primary?.site_uid ?? '',
    jobUid
  };
}
